import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import TurnstileWidget from "@/components/TurnstileWidget";

const inputCls =
  "w-full bg-white/5 border border-white/15 px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:outline-none focus:border-[#E5FF00] transition-colors duration-200";

const CATEGORY_LABELS = {
  event: "Event",
  mall: "Mall",
  cafe: "Cafe",
  gamezone: "Game Zone",
  cowork: "Co-working Space",
  hotel: "Hotel",
  transport: "Transport Hub / Bus Stop / Station",
  airport: "Airport",
  gym: "Gym",
  standalone: "Standalone Venue / Public Sight",
  religious: "Religious Place",
  university: "University",
  college: "College",
  other: "Other",
};

export default function LeadFormModal({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);
  const [captcha, setCaptcha] = useState("");
  const [meta, setMeta] = useState({ categories: [], portal_login_url: "https://lypinn.com/login" });
  const [form, setForm] = useState({ contact_name: "", contact_email: "", contact_phone: "", password: "", cc_name: "", cc_category: "mall" });

  useEffect(() => {
    if (open) {
      setDone(false);
      setResult(null);
      api.get("/leads/meta").then(({ data }) => {
        setMeta(data);
        if (data.categories?.length) setForm((f) => ({ ...f, cc_category: data.categories[0] }));
      }).catch(() => {});
    }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onToken = useCallback((token) => setCaptcha(token), []);

  const submit = async (e) => {
    e.preventDefault();
    if (!captcha) {
      toast.error("Please complete the captcha check first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/leads", { ...form, turnstile_token: captcha });
      setResult(data.detail || {});
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
      setCaptcha("");
      if (window.turnstile) window.turnstile.reset();
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 overflow-y-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          data-testid="lead-modal"
        >
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" onClick={onClose} />
          <motion.div
            className="relative w-full max-w-md bg-[#0c0c0c] border border-white/15 p-8 my-8"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors" data-testid="lead-modal-close">
              <X size={18} />
            </button>
            {done ? (
              <div data-testid="lead-success">
                <CheckCircle2 className="text-[#E5FF00] mb-6" size={40} strokeWidth={1.5} />
                <h2 className="font-display text-3xl font-bold mb-4">Application received.</h2>
                <p className="text-sm text-zinc-400 leading-relaxed mb-2">
                  Your Command Centre account is being set up. We're emailing the details to{" "}
                  <span className="text-white">{form.contact_email}</span>.
                </p>
                <p className="text-sm text-zinc-400 leading-relaxed mb-8">
                  Follow the link in that email to sign in, get your quote and sign the contract.
                </p>
                <a
                  href={result?.quote_url || result?.portal_login_url || meta.portal_login_url || "https://lypinn.com/login"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#E5FF00] text-black font-display font-bold text-sm tracking-wide px-8 py-4 hover:bg-white transition-colors"
                  data-testid="lead-portal-link"
                >
                  GO TO LYPINN LOGIN <ArrowUpRight size={15} />
                </a>
              </div>
            ) : (
              <>
                <p className="font-mono-tech text-[10px] tracking-[0.3em] text-[#E5FF00] mb-2">LYPINN PULSE</p>
                <h2 className="font-display text-3xl font-bold mb-2" data-testid="lead-modal-title">
                  Get your Command Centre
                </h2>
                <p className="text-sm text-zinc-500 mb-6 leading-relaxed">
                  Fill this in and we'll email your login details — sign in, get your quote, sign the contract.
                </p>
                <form onSubmit={submit} className="space-y-4">
                  <input type="text" required minLength={2} placeholder="Your full name" value={form.contact_name} onChange={set("contact_name")} className={inputCls} data-testid="lead-name-input" />
                  <input type="email" required placeholder="Business email (this becomes your login)" value={form.contact_email} onChange={set("contact_email")} className={inputCls} data-testid="lead-email-input" />
                  <input type="tel" required minLength={6} placeholder="Phone (e.g. +919812345678)" value={form.contact_phone} onChange={set("contact_phone")} className={inputCls} data-testid="lead-phone-input" />
                  <input type="password" required minLength={6} placeholder="Choose a password for your Command Centre login" value={form.password} onChange={set("password")} className={inputCls} data-testid="lead-password-input" />
                  <input type="text" required minLength={2} placeholder="Business, event or entity name (becomes your Command Centre name)" value={form.cc_name} onChange={set("cc_name")} className={inputCls} data-testid="lead-business-input" />
                  <select value={form.cc_category} onChange={set("cc_category")} className={`${inputCls} appearance-none`} data-testid="lead-category-select">
                    {(meta.categories?.length ? meta.categories : Object.keys(CATEGORY_LABELS)).map((c) => (
                      <option key={c} value={c} className="bg-[#0c0c0c]">
                        {CATEGORY_LABELS[c] || c}
                      </option>
                    ))}
                  </select>
                  <TurnstileWidget onToken={onToken} testid="lead-turnstile" />
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#E5FF00] text-black font-display font-bold text-sm tracking-wide py-3.5 hover:bg-white transition-colors duration-200 disabled:opacity-50"
                    data-testid="lead-submit-btn"
                  >
                    {loading ? "SUBMITTING..." : "SUBMIT APPLICATION"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
